import React, {useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {DividerContainer} from 'components/Accommodation/AccommodationView/Desktop/styles'
import {getIsRoomsResultEmpty} from 'modules/searchApi/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {HotelQuestionModal} from './HotelQuestionModal'
import {HotelQuestionViewTracker} from './HotelQuestionViewTracker'
import {hasReachedDailyMessageLimit} from './utils'

const ButtonBox = styled(Box)`
  max-width: 240px;
  width: 100%;
`

const MobileLayout: React.FC<{children: React.ReactNode}> = ({children}) => {
  return (
    <Box
      paddingTop="s300"
      paddingRight="s500"
      paddingLeft="s500"
      paddingBottom="s300"
    >
      <Box paddingBottom="s600" paddingTop="s500">
        <Divider />
      </Box>
      <Box paddingBottom="s300" paddingTop="s300">
        {children}
      </Box>
    </Box>
  )
}

const DesktopLayout: React.FC<{children: React.ReactNode}> = ({children}) => {
  const hasNoRooms = useSelector(getIsRoomsResultEmpty)
  return (
    <PageWidthContainer paddingDesktopXsSize="s500">
      <Box paddingBottom="s900">
        <Divider />
      </Box>
      {children}
      {hasNoRooms ? <Box paddingBottom="s700" /> : <DividerContainer />}
    </PageWidthContainer>
  )
}

const TitleAndDescription = ({
  setIsModalOpen
}: {
  setIsModalOpen: (isModalOpen: boolean) => void
}) => {
  const {isMobile} = useDeviceLayout()
  const dispatch = useDispatch()
  const handleButtonClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.AskChatbotButton,
        action: Action.Clicked,
        team: Team.Select
      })
    )
    setIsModalOpen(true)
  }
  return (
    <>
      <Text variant={isMobile ? 'titleL' : 'titleXL'} tag="p">
        <FormattedMessageWrapper
          defaultMessage="Have a question?"
          id="chatbot.haveAQuestion"
        />
      </Text>
      <ButtonBox paddingTop="s600" paddingBottom="s300">
        <Button
          variant="tertiary"
          onClick={handleButtonClick}
          iconStart={<Icon name="Sparkles2" />}
          fullWidth
        >
          <FormattedMessageWrapper
            defaultMessage="Ask a question"
            id="chatbot.askQuestionButton"
          />
        </Button>
      </ButtonBox>
      <Text variant="bodyS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          defaultMessage="Instant AI responses are based on hotel information"
          id="chatbot.askQuestionButtonDescription"
        />
      </Text>
    </>
  )
}

export const AskHotelQuestion = ({hotelName}: {hotelName: string}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const {isMobile} = useDeviceLayout()
  const isHydrated = useIsHydrated()
  const Layout = isMobile ? MobileLayout : DesktopLayout

  // TODO: Add condition to check if there's remaining quota for the user and budget
  const shouldRender = !hasReachedDailyMessageLimit()

  return (
    <>
      {shouldRender && (
        <Layout>
          <HotelQuestionViewTracker entity={Entity.ChatbotSection} />
          <TitleAndDescription setIsModalOpen={setIsModalOpen} />
          {isHydrated && (
            <HotelQuestionModal
              hotelName={hotelName}
              onClose={() => setIsModalOpen(false)}
              isOpen={isModalOpen}
            />
          )}
        </Layout>
      )}
    </>
  )
}
